import Menu from "@mui/material/Menu";
import { COLORS_TO_BE_REVIEWED } from "components-redesign/styles/colors";
import {
  TypographyBodySmall,
  TypographyTitleMedium,
} from "components-redesign/Typography/Typography";
import { FlexContent } from "components/FlexContent/FlexContent";
import { makeStyles } from "components/mui-core";
import useLag from "components/Reporter/DataDelayNotification/useLag";
import { HOUR_MS, HOUR_S } from "helpers";
import { ReactComponent as DataDelayedIcon } from "icons/navbar/data-delayed.svg";
import { ReactComponent as DataUpToDateIcon } from "icons/navbar/up-to-date.svg";
import { TimeRange } from "model/report/dateRange";
import { useEffect, useMemo, useState } from "react";
import { useDataStatus } from "./DataStatusContext";
import { generateLagLabel } from "./utils";

const useStyles = makeStyles()({
  badge: {
    borderRadius: "81px",
    padding: "8px",
    width: "fit-content !important",
    whiteSpace: "nowrap",
    cursor: "help",
  },
  menu: {
    marginLeft: "70px",
    marginTop: "-100px",
  },
});

interface Props {
  navbar?: boolean;
  style?;
  onActive?: (active: boolean) => void;
}

export const DataStatusBadge = ({ navbar, style, onActive }: Props) => {
  const { classes } = useStyles();
  const { dateRange, finishDate, statisticsLastUpdated } = useDataStatus();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    onActive?.(isOpen);
  }, [isOpen, onActive]);

  const isLiveMode = dateRange === TimeRange.allTime;
  const [lag, setLag] = useState(0);
  const [isDelayed, setIsDelayed] = useState(false);
  const lagValue = useLag(dateRange, finishDate);
  const [liveInterval, setLiveInterval] = useState<any>();

  useEffect(() => {
    if (isLiveMode) {
      if (statisticsLastUpdated) {
        setLag((Date.now() - statisticsLastUpdated.getTime()) / 1000);
        setIsDelayed(lag > HOUR_MS);

        if (liveInterval) {
          clearInterval(liveInterval);
        }

        setLiveInterval(
          setInterval(() => {
            if (isLiveMode) {
              setLag((Date.now() - statisticsLastUpdated.getTime()) / 1000);
            }
          }, 5000)
        );
      }
    } else {
      if (liveInterval) {
        clearInterval(liveInterval);
      }

      setIsDelayed(lagValue > HOUR_S);
      setLag(lagValue);
    }

    return () => {
      if (liveInterval) {
        clearInterval(liveInterval);
      }
    };
  }, [lagValue, isLiveMode, statisticsLastUpdated]);

  const lagLabel = useMemo(() => generateLagLabel(lag), [lag]);

  return (
    <FlexContent
      className={classes.badge}
      align="center"
      gap={"4px"}
      style={style}
      onClick={() => {
        setIsOpen(!isOpen);
      }}>
      {isDelayed ? <DataDelayedIcon /> : <DataUpToDateIcon />}
      <Menu
        PopoverClasses={{ paper: classes.menu }}
        open={isOpen}
        onClose={() => setIsOpen(false)}>
        <div style={{ padding: "14px 23px 20px 10px", width: "326px" }}>
          <FlexContent gap={"16px"}>
            <div style={{ width: "24px", height: "24px" }}>
              {isDelayed ? <DataDelayedIcon /> : <DataUpToDateIcon />}
            </div>

            <FlexContent
              gap={"8px"}
              direction="column">
              <TypographyTitleMedium>
                {isDelayed ? "Data Updates Delayed" : "Data Up to Date"}
              </TypographyTitleMedium>

              <TypographyBodySmall
                style={{ color: COLORS_TO_BE_REVIEWED.neutral70 }}>
                {isDelayed
                  ? "A delay in updates may affect reports, dashboards and visualizations. Data is still processing normally—no action needed."
                  : "Reports, dashboards, and visualizations are using the most recent data. No action needed."}
              </TypographyBodySmall>
              <TypographyBodySmall>{lagLabel}</TypographyBodySmall>
            </FlexContent>
          </FlexContent>
        </div>
      </Menu>
    </FlexContent>
  );
};
